import { Injectable, signal } from '@angular/core';
import { ToastDto } from '../shared/ui/toast/toast.component';

type ToastOptions = {
  duration?: number | null;
  dismissible?: boolean;
  action?: ToastDto['action'];
};

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly EXIT_ANIMATION_DURATION_MS = 250;
  private readonly _toasts = signal<ToastDto[]>([]);

  public toasts = this._toasts.asReadonly();

  public showToast(
    message: string,
    type: ToastDto['type'],
    options?: ToastOptions,
  ): string {
    const toast: ToastDto = {
      id: this.generateId(),
      message: message,
      type: type,
      duration: options?.duration === undefined ? 7000 : options.duration,
      dismissible: options?.dismissible,
      action: options?.action,
    };

    this._toasts.update((toasts) => [...toasts, toast]);

    if (toast.duration !== null) {
      setTimeout(() => {
        this.dismissToast(toast.id);
      }, toast.duration);
    }

    return toast.id;
  }

  public showError(message: string, duration?: number): string {
    return this.showToast(message, 'error', { duration });
  }

  public showSuccess(message: string, duration?: number): string {
    return this.showToast(message, 'success', { duration });
  }

  public showFeedback(message: string, options?: ToastOptions): string {
    this.clearAll();
    return this.showToast(message, 'feedback', options);
  }

  public dismissToast(id: string): void {
    const toast = this._toasts().find((item) => item.id === id);
    if (!toast || toast.isExiting) {
      return;
    }

    this._toasts.update((toasts) =>
      toasts.map((item) =>
        item.id === id ? { ...item, isExiting: true } : item,
      ),
    );

    setTimeout(() => {
      this._toasts.update((toasts) => toasts.filter((item) => item.id !== id));
    }, this.EXIT_ANIMATION_DURATION_MS);
  }

  public clearAll(): void {
    this._toasts.set([]);
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
}
